<template>
  <div class="arp-fill">
    <div class="arp-fill__row">
      <b-field label="Número" class="arp-fill__numero">
        <b-input :value="draft.numero" maxlength="50" @input="v => patch({ numero: v })" />
      </b-field>

      <b-field label="Ano" class="arp-fill__ano">
        <b-input :value="draft.ano" type="number" min="1999" max="9999" step="1" @input="onAno" />
      </b-field>

      <b-field label="Data de assinatura" class="arp-fill__assinatura">
        <b-datepicker
          :value="toDate(draft.dataAssinatura)"
          editable
          icon="calendar-today"
          :max-date="hoje"
          placeholder="Assinatura"
          @input="v => patch({ dataAssinatura: toIso(v) })"
        />
      </b-field>

      <DateRangePicker
        class="arp-fill__vigencia"
        label="Vigência"
        placeholder="Selecionar período"
        date-limit="any"
        dropdown-align="right"
        :show-presets="false"
        :max-span-months="12"
        :value="vigenciaRange"
        @input="onVigencia"
      />

      <b-field label="Permitir carona" class="arp-fill__carona">
        <b-switch :value="draft.permitirCarona" @input="v => patch({ permitirCarona: v })">
          {{ draft.permitirCarona ? 'Sim' : 'Não' }}
        </b-switch>
      </b-field>
    </div>

    <div class="arp-fill__arquivo">
      <b-field v-if="!isEditRascunho" label="Arquivo da ARP (PDF)">
        <b-upload
          v-model="pickerFiles"
          drag-drop
          accept=".pdf,application/pdf"
          class="arp-fill__upload"
          @input="onArquivos"
        >
          <div class="arp-fill__upload-content content has-text-centered">
            <b-icon
              class="arp-fill__upload-icon"
              icon="upload"
              size="is-medium"
              type="is-primary"
            />
            <p>Solte seu arquivo aqui ou clique para selecionar</p>
            <p class="arp-fill__upload-hint">PDF da ARP assinada</p>
          </div>
        </b-upload>
      </b-field>
      <b-message v-else type="is-info" has-icon>
        O PDF já enviado permanece. Para trocar o arquivo, exclua a ARP e cadastre novamente.
      </b-message>

      <ul v-if="arquivoSelecionado" class="arp-fill__file-list">
        <li>
          <span class="arp-fill__filename" :title="nomeArquivo">{{ nomeArquivo }}</span>
          <button
            type="button"
            class="delete is-small"
            aria-label="Remover arquivo"
            @click.prevent="removerArquivo"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DateRangePicker from '@/components/DateRangePicker/DateRangePicker';
import {
  dateToFormIsoString,
  getPncpDocumentUploadSizeErrorMessage,
  isWithinPncpDocumentUploadLimit,
  normalizeAnoArpInput,
  obterArquivoParaEnvio,
  parseIsoDateLocal,
  startOfToday,
} from '../helpers';

export default {
  name: 'ArpFillPropria',
  components: { DateRangePicker },
  data() {
    return {
      pickerFiles: [],
    };
  },
  computed: {
    ...mapGetters({
      draft: 'obterArpDraft',
      journey: 'obterArpJourney',
    }),
    isEdit() {
      return this.journey.mode === 'edit';
    },
    isEditRascunho() {
      return this.isEdit && this.journey.isRascunho !== false;
    },
    hoje() {
      return startOfToday();
    },
    arquivoSelecionado() {
      return obterArquivoParaEnvio(this.draft.arquivo);
    },
    nomeArquivo() {
      return this.arquivoSelecionado?.name || '';
    },
    vigenciaRange() {
      return {
        start: parseIsoDateLocal(this.draft.vigenciaInicio),
        end: parseIsoDateLocal(this.draft.vigenciaFim),
      };
    },
  },
  methods: {
    ...mapActions(['atualizarArpDraft']),
    patch(payload) {
      this.atualizarArpDraft(payload);
    },
    onAno(value) {
      this.patch({ ano: normalizeAnoArpInput(value) });
    },
    onArquivos(files) {
      this.pickerFiles = [];
      if (!files) return;

      const lista = Array.isArray(files) ? files : [files];
      const arquivo = lista[0];
      if (!arquivo) return;

      if (!isWithinPncpDocumentUploadLimit(arquivo.size)) {
        this.$alerta(getPncpDocumentUploadSizeErrorMessage(), 'is-danger');
        return;
      }

      const nome = String(arquivo.name || '').toLowerCase();
      if (nome && !nome.endsWith('.pdf')) {
        this.$alerta('Envie um arquivo PDF', 'is-danger');
        return;
      }

      this.patch({ arquivo });
    },
    removerArquivo() {
      this.patch({ arquivo: null });
    },
    onVigencia(range) {
      this.patch({
        vigenciaInicio: dateToFormIsoString(range && range.start),
        vigenciaFim: dateToFormIsoString(range && range.end),
      });
    },
    toDate(iso) {
      return parseIsoDateLocal(iso);
    },
    toIso(date) {
      return dateToFormIsoString(date);
    },
  },
};
</script>

<style scoped>
.arp-fill__row {
  display: grid;
  grid-template-columns:
    minmax(88px, 0.7fr)
    minmax(72px, 0.55fr)
    minmax(160px, 1.1fr)
    minmax(200px, 1.6fr)
    auto;
  gap: 0.75rem 0.85rem;
  align-items: start;
}

.arp-fill__row >>> .field {
  margin-bottom: 0;
  width: 100%;
}

.arp-fill__row >>> .label {
  display: block;
  margin-bottom: 0.5rem;
  min-height: 1.5em;
  line-height: 1.5em;
  white-space: nowrap;
}

.arp-fill__row >>> .control,
.arp-fill__row >>> .select,
.arp-fill__row >>> .select select,
.arp-fill__row >>> .input,
.arp-fill__row >>> .datepicker {
  width: 100%;
  max-width: 100%;
}

.arp-fill__row >>> .select select,
.arp-fill__row >>> .input,
.arp-fill__row >>> .datepicker .input,
.arp-fill__vigencia >>> .date-range-picker__trigger {
  height: 2.5em;
}

.arp-fill__carona {
  min-width: 7.5rem;
  white-space: nowrap;
}

.arp-fill__carona >>> .control {
  display: flex;
  align-items: center;
  min-height: 2.5em;
}

.arp-fill__carona >>> .switch {
  white-space: nowrap;
}

.arp-fill__arquivo {
  margin-top: 1rem;
}

.arp-fill__upload {
  display: block;
  width: 100%;
}

.arp-fill__upload >>> .upload-draggable {
  width: 100%;
}

.arp-fill__upload-content {
  align-items: center;
  background: #fafafa;
  border: 2px dashed #dbdbdb;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 140px;
  padding: 1rem;
  text-align: center;
}

.arp-fill__upload-icon {
  margin-bottom: 0.5rem;
}

.arp-fill__upload-hint {
  color: #8b8b8b;
  font-size: 0.85rem;
  margin-bottom: 0 !important;
  margin-top: 0.35rem !important;
}

.arp-fill__file-list {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.arp-fill__file-list li {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  padding: 0.35rem 0;
}

.arp-fill__filename {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .arp-fill__row {
    grid-template-columns:
      minmax(80px, 0.6fr)
      minmax(70px, 0.5fr)
      minmax(140px, 1fr)
      minmax(180px, 1.4fr)
      auto;
    gap: 0.75rem 0.6rem;
  }
}

@media (max-width: 900px) {
  .arp-fill__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .arp-fill__assinatura,
  .arp-fill__vigencia {
    grid-column: 1 / -1;
  }
}

@media (max-width: 520px) {
  .arp-fill__row {
    grid-template-columns: minmax(0, 1fr);
  }

  .arp-fill__assinatura,
  .arp-fill__vigencia,
  .arp-fill__carona {
    grid-column: auto;
  }
}
</style>
